import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, X, Lock } from "lucide-react";

/**
 * Barra fixa exibida no topo do dashboard quando o assinante está em trial.
 * - Cor amarela quando faltam > 2 dias
 * - Cor vermelha (aviso) quando faltam <= 2 dias
 * Se o trial já expirou, dispara o modal "Assine um plano" em tela cheia.
 */
export function TrialBanner({ user }) {
  const navigate = useNavigate();
  if (!user || user.super_admin) return null;
  if (user.plano !== "trial") return null;
  const dias = user.trial_dias_restantes;
  if (dias == null) return null;

  const criticalMode = dias <= 2;
  const bg = criticalMode ? "bg-red-600" : "bg-amber-500";

  return (
    <div className={`${bg} text-white px-4 py-2 text-sm font-bold flex items-center justify-center gap-2 sticky top-0 z-40`} data-testid="vaipede-trial-banner">
      <Sparkles className="w-4 h-4 shrink-0"/>
      <span>
        🎁 Trial gratuito · <b>{dias === 1 ? "1 dia restante" : `${dias} dias restantes`}</b>
      </span>
      <button
        onClick={() => navigate("/vaipede/dashboard/config")}
        className="ml-2 px-3 py-0.5 rounded-full bg-white text-neutral-900 hover:bg-neutral-100 transition text-xs font-black"
        data-testid="vaipede-trial-cta"
      >
        Assinar agora
      </button>
    </div>
  );
}

/**
 * Modal em tela cheia disparado quando trial expirou (via evento `vaipede:trial-expired`
 * emitido pelo interceptor axios em qualquer HTTP 402). Fecha só ao clicar em "Assinar".
 */
export function TrialExpiredModal() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("vaipede:trial-expired", handler);
    return () => window.removeEventListener("vaipede:trial-expired", handler);
  }, []);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 grid place-items-center p-4" data-testid="vaipede-trial-modal">
      <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl relative">
        <div className="w-16 h-16 rounded-full bg-red-100 text-[#EA1D2C] grid place-items-center mx-auto mb-4">
          <Lock className="w-8 h-8"/>
        </div>
        <h2 className="text-2xl font-black text-center mb-2">Trial gratuito encerrado</h2>
        <p className="text-neutral-600 text-center text-sm mb-6">
          Seus 7 dias de teste do VAI PEDE terminaram. Escolha um plano para continuar usando o sistema sem perder os dados da sua loja.
        </p>
        <div className="space-y-2">
          <button
            onClick={() => { setOpen(false); navigate("/vaipede/dashboard/config"); }}
            className="w-full py-3.5 rounded-xl bg-[#EA1D2C] hover:bg-[#c8121f] text-white font-black text-sm"
            data-testid="vaipede-trial-modal-cta"
          >
            Escolher meu plano
          </button>
          <button
            onClick={() => setOpen(false)}
            className="w-full py-2.5 rounded-xl text-neutral-500 text-xs font-bold"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
